"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useBranchEnergyCalculation = void 0;
const react_1 = require("react");
const types_1 = require("../../types");
const useSensorEvents_1 = require("../useSensorEvents");
const NUMBER_OF_DATAPOINTS_FOR_REALTIME_CALCULATION = 6;
const useBranchEnergyCalculation = ({ selectedHub, sensors }) => {
    const [branchEnergyCalculationData, setBranchEnergyCalculationData] = (0, react_1.useState)([]);
    const [branchPowerMonitorData, setBranchPowerMonitorData] = (0, react_1.useState)([]);
    const [isValidConfiguration, setIsValidConfiguration] = (0, react_1.useState)(false);
    const powerMonitorSensor = (0, react_1.useMemo)(() => sensors.find((sensor) => sensor.hubId === selectedHub?.id && sensor.type === types_1.SensorType.POWER), [sensors, selectedHub]);
    const { sensorEvents, fetchSensorEventsBySensor, loading, error } = (0, useSensorEvents_1.useSensorEvents)(selectedHub?.id, powerMonitorSensor?.id);
    const fetchReadings = (0, react_1.useCallback)(async () => {
        if (!powerMonitorSensor) {
            return Promise.resolve();
        }
        return await fetchSensorEventsBySensor(powerMonitorSensor?.id, NUMBER_OF_DATAPOINTS_FOR_REALTIME_CALCULATION);
    }, [powerMonitorSensor, fetchSensorEventsBySensor]);
    const processReadings = () => {
        if (!powerMonitorSensor ||
            !powerMonitorSensor.branchCircuits ||
            powerMonitorSensor.branchCircuits.length === 0) {
            setIsValidConfiguration(false);
            return;
        }
        setIsValidConfiguration(true);
        if (!sensorEvents) {
            return;
        }
        const branchEnergyCalculationData = processBranchEnergyCalculation(sensorEvents, powerMonitorSensor.branchCircuits || []);
        const branchPowerMonitorData = processBranchPowerMonitorReadings(sensorEvents, powerMonitorSensor.branchCircuits || []);
        setBranchEnergyCalculationData(branchEnergyCalculationData);
        setBranchPowerMonitorData(branchPowerMonitorData);
    };
    (0, react_1.useEffect)(() => {
        fetchReadings();
    }, [powerMonitorSensor]);
    (0, react_1.useEffect)(() => {
        processReadings();
    }, [powerMonitorSensor, sensorEvents]);
    return {
        powerMonitorSensor,
        branchEnergyCalculationData,
        branchPowerMonitorData,
        isValidConfiguration,
        refresh: fetchReadings,
        loading,
        error,
    };
};
exports.useBranchEnergyCalculation = useBranchEnergyCalculation;
function processBranchEnergyCalculation(events, branchCircuits) {
    events.sort((a, b) => a.timestamp - b.timestamp);
    const packetBuffer = buildPacketData(events);
    const readings = buildPowerMonitorReading(packetBuffer);
    const calculations = calculateBranchEnergyPercentFromReadings(readings, branchCircuits);
    return calculations;
}
function processBranchPowerMonitorReadings(events, branchCircuits) {
    const sortedEvents = [...events].sort((a, b) => a.timestamp - b.timestamp);
    const packetBuffer = buildPacketData(sortedEvents);
    const readings = buildPowerMonitorReading(packetBuffer);
    if (readings.length === 0) {
        return [];
    }
    const latestReading = readings[readings.length - 1];
    const branchPowers = calculateAccumulatedPowerFromReadings([latestReading]);
    const totalPower = calculateTotalPower(branchPowers);
    const branchData = extractBranchDataFromReading(latestReading);
    return branchData
        .map(({ ct, voltage, inputNumber }) => {
        const circuit = branchCircuits.find((c) => c.powerMonitorInputNumber === inputNumber);
        if (!circuit || (!circuit.sublocation && !circuit.location)) {
            return null;
        }
        const power = branchPowers[inputNumber - 1];
        const percent = calculatePowerPercentage(power, totalPower);
        return {
            branchIndex: inputNumber,
            branchCT: ct,
            voltage,
            percent,
            name: circuit.sublocation || circuit.location,
            location: circuit.location,
            sublocation: circuit.sublocation,
            timestamp: latestReading.timestamp,
        };
    })
        // Filter out branches that are not configured
        .filter(Boolean);
}
function buildPacketData(events) {
    const packetBuffer = [];
    for (const event of events) {
        const part = event.values?.packet_type;
        const values = event.values;
        const sequence = event.parsedMetadata?.sequenceNumber;
        if (!part || !values || typeof sequence !== 'number')
            continue;
        const newValues = { ...values };
        delete newValues.packet_type;
        packetBuffer.push({
            part,
            values: newValues,
            sequence,
            timestamp: event.timestamp,
        });
    }
    return packetBuffer;
}
function buildPowerMonitorReading(packetBuffer) {
    const readings = [];
    for (let i = 0; i < packetBuffer.length; i++) {
        const a = packetBuffer[i];
        if (a.part !== 'A')
            continue;
        const b = packetBuffer.find((p) => p.sequence === a.sequence + 1 && p.part === 'B');
        const c = packetBuffer.find((p) => p.sequence === a.sequence + 2 && p.part === 'C');
        if (b && c) {
            readings.push({
                ...a.values,
                ...b.values,
                ...c.values,
                timestamp: a.timestamp,
            });
        }
    }
    return readings;
}
function calculatePowerPercentage(power, totalPower) {
    return totalPower > 0 ? parseFloat(((power / totalPower) * 100).toFixed(2)) : 0;
}
function calculateTotalPower(powerValues) {
    return powerValues.reduce((sum, val) => sum + val, 0);
}
function extractBranchDataFromReading(reading) {
    return [
        { ct: reading.branch_ct1, voltage: reading.vt1, inputNumber: 1 },
        { ct: reading.branch_ct2, voltage: reading.vt1, inputNumber: 2 },
        { ct: reading.branch_ct3, voltage: reading.vt1, inputNumber: 3 },
        { ct: reading.branch_ct4, voltage: reading.vt1, inputNumber: 4 },
        { ct: reading.branch_ct5, voltage: reading.vt1, inputNumber: 5 },
        { ct: reading.branch_ct6, voltage: reading.vt1, inputNumber: 6 },
        { ct: reading.branch_ct7, voltage: reading.vt1, inputNumber: 7 },
        { ct: reading.branch_ct8, voltage: reading.vt2, inputNumber: 8 },
        { ct: reading.branch_ct9, voltage: reading.vt2, inputNumber: 9 },
        { ct: reading.branch_ct10, voltage: reading.vt2, inputNumber: 10 },
        { ct: reading.branch_ct11, voltage: reading.vt2, inputNumber: 11 },
        { ct: reading.branch_ct12, voltage: reading.vt2, inputNumber: 12 },
        { ct: reading.branch_ct13, voltage: reading.vt2, inputNumber: 13 },
        { ct: reading.branch_ct14, voltage: reading.vt2, inputNumber: 14 },
        { ct: reading.branch_ct15, voltage: reading.vt2, inputNumber: 15 },
        { ct: reading.branch_ct16, voltage: reading.vt2, inputNumber: 16 },
    ];
}
function calculateAccumulatedPowerFromReadings(readings) {
    const totals = new Array(16).fill(0);
    for (const reading of readings) {
        const branchData = extractBranchDataFromReading(reading);
        branchData.forEach((branch, idx) => {
            totals[idx] += branch.ct * branch.voltage;
        });
    }
    return totals;
}
function calculateBranchEnergyPercentFromReadings(readings, branchCircuits) {
    if (readings.length === 0) {
        return [];
    }
    const latestReading = readings[readings.length - 1];
    const branchPowers = calculateAccumulatedPowerFromReadings([latestReading]);
    const totalPower = calculateTotalPower(branchPowers);
    return branchPowers.map((power, index) => {
        const inputNumber = index + 1;
        const circuit = branchCircuits.find((c) => c.powerMonitorInputNumber === inputNumber);
        return {
            branchIndex: inputNumber,
            power: parseFloat(power.toFixed(2)),
            percent: calculatePowerPercentage(power, totalPower),
            name: circuit?.sublocation || circuit?.location || `branch_ct${inputNumber}`,
            location: circuit?.location,
            sublocation: circuit?.sublocation,
        };
    });
}
